import { NodeSelection, type Selection } from '@tiptap/pm/state';
import {
  Editor,
  isTextSelection,
  isNodeSelection,
  posToDOMRect,
} from '@tiptap/react';

const NODE_TYPE_LABELS: Record<string, string> = {
  paragraph: 'text',
  codeBlock: 'Codeblock',
};

export type OverflowPosition = 'none' | 'top' | 'bottom' | 'both';
/**
 * Determines how a target element overflows relative to a container element
 */
export function getElementOverflowPosition(
  targetElement: Element,
  containerElement: HTMLElement
): OverflowPosition {
  const targetBounds = targetElement.getBoundingClientRect();
  const containerBounds = containerElement.getBoundingClientRect();

  const isOverflowingTop = targetBounds.top < containerBounds.top;
  const isOverflowingBottom = targetBounds.bottom > containerBounds.bottom;

  if (isOverflowingTop && isOverflowingBottom) {
    return 'both';
  }
  if (isOverflowingTop) {
    return 'top';
  }
  if (isOverflowingBottom) {
    return 'bottom';
  }
  return 'none';
}

/**
 * Generates a deterministic avatar URL from a user name
 */
export const getAvatar = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const randomFraction = (Math.abs(hash) % 1000000) / 1000000;
  const id = 1 + Math.floor(randomFraction * 20);
  const idString = id.toString().padStart(2, '0');
  return `/avatars/memoji_${idString}.png`;
};

/**
 * Returns a display name for the current node in the editor
 * @param editor The Tiptap editor instance
 * @returns The display name of the current node
 */
export const getNodeDisplayName = (editor: Editor | null): string => {
  if (!editor) {
    return 'Node';
  }

  const { selection } = editor.state;

  if (selection instanceof NodeSelection) {
    const nodeType = selection.node.type.name;
    return NODE_TYPE_LABELS[nodeType] || nodeType.toLowerCase();
  }

  const { $anchor } = selection;
  const nodeType = $anchor.parent.type.name;
  return NODE_TYPE_LABELS[nodeType] || nodeType.toLowerCase();
};

/**
 * Checks if the current text selection is valid for editing
 * - Not empty
 * - Not a code block
 * - Not a node selection
 */
export const isTextSelectionValid = (editor: Editor | null): boolean => {
  if (!editor) {
    return false;
  }
  const { state } = editor;
  const { selection } = state;
  const isValid =
    isTextSelection(selection) &&
    !selection.empty &&
    !selection.$from.parent.type.spec.code &&
    !isNodeSelection(selection);

  return isValid;
};

/**
 * Checks if the current selection is valid for a given editor
 */
export const isSelectionValid = (
  editor: Editor | null,
  selection?: Selection,
  excludedNodeTypes: string[] = ['imageUpload', 'horizontalRule']
): boolean => {
  if (!editor) {
    return false;
  }
  if (!selection) {
    selection = editor.state.selection;
  }

  const { state } = editor;
  const { doc } = state;
  const { empty, from, to } = selection;

  const isEmptyTextBlock =
    !doc.textBetween(from, to).length && isTextSelection(selection);
  const isCodeBlock =
    selection.$from.parent.type.spec.code ||
    (isNodeSelection(selection) && selection.node.type.spec.code);
  const isExcludedNode =
    isNodeSelection(selection) &&
    excludedNodeTypes.includes(selection.node.type.name);

  return !empty && !isEmptyTextBlock && !isCodeBlock && !isExcludedNode;
};

/**
 * Gets the bounding rect of the current selection in the editor.
 */
export const getSelectionBoundingRect = (editor: Editor): DOMRect | null => {
  const { state } = editor.view;
  const { selection } = state;
  const { ranges } = selection;

  const from = Math.min(...ranges.map((range) => range.$from.pos));
  const to = Math.max(...ranges.map((range) => range.$to.pos));

  if (isNodeSelection(selection)) {
    const node = editor.view.nodeDOM(from) as HTMLElement;
    if (node != null) {
      return node.getBoundingClientRect();
    }
  }

  return posToDOMRect(editor.view, from, to);
};
